import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import styles from '../../styles/mariana-main-chat.css?inline';
import { type ChatMessage, type ButtonOption, type Recipe, type Coupon, type Shop, type ImageItem } from './types.js';
import { createChatMessage, scrollToBottom, dispatchChatMarianaLinks, hasInteractiveActions } from '../../utils/chat-utils.js';

import { renderMessage, renderTypingIndicator } from './renderers/message-renderer.js';
import { renderChatHeader } from './renderers/header-renderer.js';
import { renderInputArea } from './renderers/input-renderer.js';
import { ChatConnection } from './chat-connection.js';
import { UserInteractionHandler } from './user-interaction-handler.js';
import { NetworkStateManager } from './network-state-manager.js';
import { KeyboardVisibilityManager } from './keyboard-visibility-manager.js';
import { config } from '../../config/environment.js';
import { chatService } from '../../services/chat-service.js';
import { getChatId, clearChatId, clearReconnectSecret } from '../../services/session-storage.js';
import { createConnectionCallbacks } from './connection-callbacks.js';
import { handleCouponSelectionChanged, getSelectedCouponCount } from './coupon-handler.js';
import { animateHistoryScrollToBottom, buildMessagesFromReconnectHistory } from './reconnect-history.js';
import { ActionLockManager } from './action-lock-manager.js';
import '../buttons/mariana-normal-buttons.js';
import '../buttons/mariana-hierarchy-buttons.js';
import '../buttons/mariana-list-button.js';
import '../buttons/mariana-csat-buttons.js';
import '../cards/mariana-shop-detail-sheet.js';

@customElement('mariana-chat')
export class MarianaChat extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: String, reflect: true })
  source = '';

  @state() private messages: ChatMessage[] = [];
  @state() private isTyping = false;
  @state() private isBufferingInitial = false;
  @state() private wsConnected = false;
  @state() private inputValue = '';
  @state() private showDropdown = false;
  @state() private isResponding = false;
  @state() private isReconnecting = false;
  @state() private selectedCouponIds: string[] = [];
  @state() private isKeyboardOpen = false;
  @state() private selectedShop: Shop | null = null;
  @state() private isShopDetailOpen = false;
  private shopDetailTriggerEl: HTMLElement | null = null;
  private shopCardsHostEl: HTMLElementTagNameMap['mariana-shop-cards'] | null = null;
  private pendingDesktopHandoffShop: Shop | null = null;
  private readonly desktopMediaQuery = globalThis.matchMedia('(min-width: 769px)');

  private readonly networkState = new NetworkStateManager();
  private readonly actionLockManager = new ActionLockManager();
  private readonly user = 'user';
  private connection?: ChatConnection;
  private interactionHandler?: UserInteractionHandler;
  private readonly keyboardVisibilityManager = new KeyboardVisibilityManager({
    getShadowRoot: () => this.shadowRoot,
    onKeyboardVisibilityChange: (isOpen) => { this.isKeyboardOpen = isOpen; },
  });

  private readonly handleCouponSelectionChanged = (e: Event) =>
    handleCouponSelectionChanged(e, {
      getMessages: () => this.messages,
      getSelectedIds: () => this.selectedCouponIds,
      setSelectedIds: ids => { this.selectedCouponIds = ids; },
    });

  private readonly handleShopMoreInfo = (e: Event): void => {
    const shop = (e as CustomEvent<{ shop: Shop }>).detail?.shop;
    if (!shop) return;

    const path = e.composedPath();
    this.shopDetailTriggerEl = path
      .find((el): el is HTMLElement => el instanceof HTMLElement && el.classList.contains('shop-cta--info')) ?? null;
    this.shopCardsHostEl = path
      .find((el): el is HTMLElementTagNameMap['mariana-shop-cards'] => el instanceof HTMLElement && el.tagName === 'MARIANA-SHOP-CARDS') ?? null;

    this.selectedShop = shop;
    this.isShopDetailOpen = true;
  };

  private readonly handleShopDetailClose = (): void => {
    this.isShopDetailOpen = false;
  };

  private readonly handleShopDetailClosed = (): void => {
    this.selectedShop = null;
    this.shopDetailTriggerEl?.focus();
    this.shopDetailTriggerEl = null;

    if (this.pendingDesktopHandoffShop) {
      const shop = this.pendingDesktopHandoffShop;
      this.pendingDesktopHandoffShop = null;
      this.shopCardsHostEl?.openInlineDetail(shop);
    }
  };

  /**
   * If the viewport crosses into desktop width while the mobile modal sheet is
   * open, close it and hand off to the desktop inline detail on the shop card
   * that originally opened it, instead of leaving the modal up on a wide screen.
   */
  private readonly handleDesktopMediaChange = (e: MediaQueryListEvent): void => {
    if (!e.matches || !this.isShopDetailOpen || !this.selectedShop) return;

    this.pendingDesktopHandoffShop = this.selectedShop;
    this.handleShopDetailClose();
  };

  private readonly handleOnlineBeforeSessionStart = (): void => {
    if (this.networkState.handleOnline()) void this.startConnection();
  };

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.networkState.handlePageHidden();
      return;
    }

    this.networkState.handlePageVisible();
  };

  private readonly handleWindowPointerDown = (e: PointerEvent): void => {
    if (!this.showDropdown) return;
    const headerControls = this.shadowRoot?.querySelector('.header-controls');
    if (!headerControls) return;

    const eventPath = e.composedPath();
    if (eventPath.includes(headerControls)) return;

    this.showDropdown = false;
  };

  private get selectedCouponCount(): number {
    return getSelectedCouponCount({
      getMessages: () => this.messages,
      getSelectedIds: () => this.selectedCouponIds,
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('coupon-selection-changed', this.handleCouponSelectionChanged);
    this.addEventListener('shop-more-info', this.handleShopMoreInfo);
    this.desktopMediaQuery.addEventListener('change', this.handleDesktopMediaChange);
    globalThis.addEventListener('online', this.handleOnlineBeforeSessionStart);
    globalThis.addEventListener('pointerdown', this.handleWindowPointerDown);
    this.keyboardVisibilityManager.start();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.handleVisibilityChange();

    this.interactionHandler = new UserInteractionHandler({
      senderId: this.user,
      getInputValue: () => this.inputValue,
      setInputValue: value => { this.inputValue = value; },
      hasEstablishedSession: () => this.networkState.getHasEstablishedSession(),
      isBufferingInitial: () => this.isBufferingInitial,
      isConnected: () => this.wsConnected,
      getIsOffline: () => this.networkState.getIsOffline(),
      onOfflineInput: () => this.addOfflineMessage(),
      getIsResponding: () => this.isResponding,
      addMessage: (message, sender, direction, buttons) => this.addMessage(message, sender, direction, buttons),
      setIsTyping: isTyping => { this.isTyping = isTyping; },
      setIsResponding: isResponding => { this.isResponding = isResponding; },
      getSelectedValues: () => [...this.selectedCouponIds],
      clearSelectedValues: () => { this.selectedCouponIds = []; },
      onSendError: () => {
        this.addMessage(config.MESSAGES.GENERIC_ERROR_MESSAGE, 'system', 'incoming');
      },
      attemptReconnect: () => this.attemptReconnect(),
    });

    this.connection = new ChatConnection(
      { source: this.source },
      createConnectionCallbacks({
        networkState: this.networkState,
        source: this.source,
        getIsReconnecting: () => this.isReconnecting,
        setWsConnected: v => { this.wsConnected = v; },
        setIsTyping: v => { this.isTyping = v; },
        setIsResponding: v => { this.isResponding = v; },
        setIsReconnecting: v => { this.isReconnecting = v; },
        addMessage: (...args) => this.addMessage(...args),
        appendMessages: (messages) => {
          if (!messages.length) return;
          this.messages = [...this.messages, ...messages];
          scrollToBottom(this.shadowRoot);
        },
        appendInitialMessage: msg => { this.messages = [...this.messages, msg]; },
        markInitialMessageShown: () => this.networkState.markInitialMessageShown(),
        setIsBufferingInitial: v => { this.isBufferingInitial = v; },
        addOfflineMessage: () => this.addOfflineMessage(),
      })
    );

    void this.startConnection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('coupon-selection-changed', this.handleCouponSelectionChanged);
    this.removeEventListener('shop-more-info', this.handleShopMoreInfo);
    this.desktopMediaQuery.removeEventListener('change', this.handleDesktopMediaChange);
    globalThis.removeEventListener('online', this.handleOnlineBeforeSessionStart);
    globalThis.removeEventListener('pointerdown', this.handleWindowPointerDown);
    this.keyboardVisibilityManager.stop();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.connection?.disconnect();
  }

  public focusInput(): void {
    this.shadowRoot?.querySelector<HTMLTextAreaElement>('.message-input')?.focus();
  }

  protected updated(changedProperties: Map<string, unknown>): void {
    // Reset the textarea back to single-line height once the message is sent and cleared,
    // since the auto-grow height set during typing is plain inline style, not state-driven.
    if (changedProperties.has('inputValue') && this.inputValue === '') {
      const textarea = this.shadowRoot?.querySelector<HTMLTextAreaElement>('.message-input');
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.closest('.input-wrapper')?.classList.remove('input-wrapper--multiline');
      }
    }
  }

  private addMessage(
    message: string,
    sender: string,
    direction: 'incoming' | 'outgoing',
    hierarchy_buttons?: ButtonOption[],
    normal_buttons?: ButtonOption[],
    list_buttons?: ButtonOption[],
    extras?: { csat?: ButtonOption[]; recipes?: Recipe[]; coupons?: Coupon[]; shops?: Shop[]; image?: ImageItem }
  ) {
    const newMessage = createChatMessage(message, sender, direction, hierarchy_buttons, normal_buttons, list_buttons);
    newMessage.csat = extras?.csat;
    newMessage.recipes = extras?.recipes;
    newMessage.coupons = extras?.coupons;
    newMessage.shops = extras?.shops;
    newMessage.image = extras?.image;
    this.messages = [...this.messages, newMessage];
    scrollToBottom(this.shadowRoot);
  }

  private async attemptReconnect(): Promise<boolean> {
    if (this.isReconnecting || !this.connection) return false;

    const hasEstablished = this.networkState.getHasEstablishedSession();
    const { canAttempt } = this.networkState.attemptReconnect(hasEstablished);
    if (!canAttempt) return false;

    this.isReconnecting = true;
    try {
      const success = hasEstablished
        ? await this.connection.reconnect()
        : await this.connection.connect();

      this.isReconnecting = false;
      if (!success) this.addMessage(config.MESSAGES.GENERIC_ERROR_MESSAGE, 'system', 'incoming');
      return success;
    } catch {
      this.isReconnecting = false;
      this.addMessage(config.MESSAGES.GENERIC_ERROR_MESSAGE, 'system', 'incoming');
      return false;
    }
  }

  private addOfflineMessage(): void {
    const lastMessage = this.messages[this.messages.length - 1];
    if (lastMessage?.sender === 'system' && lastMessage.message === config.MESSAGES.OFFLINE_NETWORK_MESSAGE) return;
    this.addMessage(config.MESSAGES.OFFLINE_NETWORK_MESSAGE, 'system', 'incoming');
  }

  private async startConnection(): Promise<void> {
    if (!this.connection || this.wsConnected) return;

    const { canStart } = this.networkState.startConnection();
    if (!canStart) {
      const isInitialOffline =
        !this.networkState.getHasEstablishedSession() &&
        (!navigator.onLine || this.networkState.getIsOffline());
      if (isInitialOffline) this.addOfflineMessage();
      return;
    }

    const savedChatId = getChatId();

    const typingDelay = setTimeout(() => { this.isTyping = true; }, 500);
    const stopTyping = () => { clearTimeout(typingDelay); this.isTyping = false; };

    if (savedChatId) {
      // Attempt to reconnect to existing session
      try {
        this.isReconnecting = true;
        const reconnected = await this.connection.reconnectFromStorage();
        this.isReconnecting = false;
        if (reconnected) {
          stopTyping();
          this.isBufferingInitial = false;
          // Prevent duplicate greeting on reconnect: history includes the initial message.
          this.networkState.markInitialMessageShown();
          this.messages = buildMessagesFromReconnectHistory(
            chatService.getReconnectMessages(),
            chatService.getReconnectChatCreatedAt(),
            this.source
          );
          await this.updateComplete;
          animateHistoryScrollToBottom(this.shadowRoot);
          this.networkState.finishConnection(true);
          return;
        }
      } catch {
        this.isReconnecting = false;
        // fall through to create new session
      }

      clearReconnectSecret();
      clearChatId();
      this.messages = [];
    }

    try {
      const connected = await this.connection.connect();
      stopTyping();
      this.networkState.finishConnection(connected);
    } catch {
      stopTyping();
      this.networkState.finishConnection(false);
    }
  }

  private handleToggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  private handleMinimize() {
    this.dispatchEvent(new CustomEvent('minimize'));
    this.showDropdown = false;
  }

  private handleClose() {
    this.emitChatTerminated();
    this.dispatchEvent(new CustomEvent('close'));
    this.showDropdown = false;
  }

  private get isAppCC(): boolean {
    return this.source.toLowerCase() === 'appcc';
  }

  private readonly handleMarkdownLinkClick = (e: Event) => {
    if (!this.isAppCC) return;
    const target = (e.target as HTMLElement).closest('a');
    if (!target) return;
    if (!target.closest('.message-bubble')) return;
    const url = target.getAttribute('href') ?? '';
    e.preventDefault();
    dispatchChatMarianaLinks(url);
  };

  private readonly handleMessageListInteraction = (e: Event) => {
    if (this.actionLockManager.isBlockedInteraction(e)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    this.handleMarkdownLinkClick(e);
  };

  private emitChatTerminated() {
    window.postMessage({ type: 'CHAT_TERMINATED', action: '' }, globalThis.location.origin);
  }

  /**
   * Derives actionsLocked for every message: an incoming action block is locked
   * when any outgoing message appears after it in the conversation.
   */
  private get messagesWithLockState(): ChatMessage[] {
    let seenOutgoingAfter = false;
    let lastOutgoingText = '';
    const result = [...this.messages].reverse().map(msg => {
      if (msg.direction === 'outgoing') {
        seenOutgoingAfter = true;
        lastOutgoingText = msg.message;
        return msg;
      }
      if (hasInteractiveActions(msg)) {
        return {
          ...msg,
          actionsLocked: seenOutgoingAfter,
          lockedSelectedValue: seenOutgoingAfter ? lastOutgoingText : undefined,
        };
      }
      return msg;
    });
    return result.reverse();
  }

  render() {
    return html`
      <div
        class="chat-container ${this.isKeyboardOpen ? 'keyboard-open' : ''}"
        @focusin=${this.keyboardVisibilityManager.handleFocusIn}
        @focusout=${this.keyboardVisibilityManager.handleFocusOut}
      >
        ${renderChatHeader(
      this.showDropdown,
      () => this.handleToggleDropdown(),
      () => this.handleMinimize(),
      () => this.handleClose()
    )}
        <div class="message-list" @click=${this.handleMessageListInteraction} @change=${this.handleMessageListInteraction}>
          ${this.messagesWithLockState.map(msg => renderMessage(msg, (e) => this.interactionHandler?.handleButtonClick(e), this.selectedCouponCount, this.source))}
          ${(this.isTyping || this.isBufferingInitial) ? renderTypingIndicator() : ''}
        </div>
        ${renderInputArea(
      this.inputValue,
      (e) => this.interactionHandler?.handleInput(e),
      (e) => this.interactionHandler?.handleKeyPress(e),
      () => this.interactionHandler?.handleSend(),
      this.isResponding,
      this.networkState.getHasEstablishedSession() && !this.isBufferingInitial
    )}
        ${this.selectedShop ? html`
          <mariana-shop-detail-sheet
            .shop=${this.selectedShop}
            ?open=${this.isShopDetailOpen}
            .source=${this.source}
            @shop-sheet-close=${this.handleShopDetailClose}
            @shop-sheet-closed=${this.handleShopDetailClosed}
          ></mariana-shop-detail-sheet>
        ` : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mariana-chat': MarianaChat;
  }
}